import { forwardRef, useLayoutEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react'
import './ui.css'

type PillProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'soft' | 'primary' | 'selected' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  icon?: ReactNode
}

const cx = (...names: (string | undefined | false)[]) => names.filter(Boolean).join(' ')

/** 胶囊按钮。每屏最多一个 primary（黄底深字）。 */
export const Pill = forwardRef<HTMLButtonElement, PillProps>(function Pill({ variant = 'soft', size = 'md', icon, children, type = 'button', className, ...rest }, ref) {
  return (
    <button ref={ref} type={type} {...rest} className={cx('sk-pill', className)} data-variant={variant} data-size={size}>
      {icon}
      {children}
    </button>
  )
})

type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { label: string; variant?: 'plain' | 'primary' }

/** 圆形图标按钮。label 是读屏读到的名字，也是悬停提示。 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton({ label, variant = 'plain', children, type = 'button', className, ...rest }, ref) {
  return (
    <button ref={ref} type={type} aria-label={label} title={label} {...rest} className={cx('sk-icon-btn', className)} data-variant={variant}>
      {children}
    </button>
  )
})

export function Chip({ children, title }: { children: ReactNode; title?: string }) {
  return (
    <span className="sk-chip" title={title}>
      {children}
    </span>
  )
}

export function Citation({ n, onOpen, label }: { n: number; onOpen?: () => void; label?: string }) {
  return (
    <button type="button" className="sk-cite" onClick={onOpen} aria-label={label ?? `引用 ${n}`}>
      {n}
    </button>
  )
}

export interface SegItem<T extends string> {
  value: T
  label: ReactNode
  icon?: ReactNode
}

/** 分段页签：滑块滑到选中的那一项。键盘左右键切换。 */
export function Segmented<T extends string>({ items, value, onChange, label }: { items: SegItem<T>[]; value: T; onChange: (v: T) => void; label: string }) {
  const track = useRef<HTMLDivElement>(null)
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null)

  useLayoutEffect(() => {
    const el = track.current?.querySelector<HTMLElement>('[aria-selected="true"]')
    if (!el) return
    const place = () => setThumb({ left: el.offsetLeft, width: el.offsetWidth })
    place()
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(place) : null
    ro?.observe(el)
    return () => ro?.disconnect()
  }, [value, items])

  const move = (dir: 1 | -1) => {
    const i = items.findIndex((it) => it.value === value)
    const next = items[(i + dir + items.length) % items.length]
    onChange(next.value)
    track.current?.querySelector<HTMLElement>(`[data-value="${next.value}"]`)?.focus()
  }

  return (
    <div ref={track} className="sk-seg" role="tablist" aria-label={label}>
      {thumb && <span className="sk-seg-thumb" aria-hidden="true" style={{ left: thumb.left, width: thumb.width }} />}
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          role="tab"
          className="sk-seg-item"
          data-value={it.value}
          aria-selected={it.value === value}
          tabIndex={it.value === value ? 0 : -1}
          onClick={() => onChange(it.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') { e.preventDefault(); move(1) }
            if (e.key === 'ArrowLeft') { e.preventDefault(); move(-1) }
          }}
        >
          {it.icon}
          {it.label}
        </button>
      ))}
    </div>
  )
}
